import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { AbigailBackdrop } from "@/components/abigail/AbigailBackdrop";
import { Wordmark } from "@/components/abigail/Wordmark";
import { api, ApiError } from "@/lib/api";
import { fetchMe, meQueryKey } from "@/lib/auth";
import { useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/entrar")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search["token"] === "string" ? (search["token"] as string) : undefined,
  }),
  component: EntrarPage,
});

/**
 * Landing after external auth flows:
 *  - When called with `?token=…` it redeems the magic-link token, opens a
 *    session, and forwards to /bem-vindo.
 *  - Without a token (e.g. the Hotmart SSO callback that already set a session
 *    cookie), it verifies /api/auth/me and forwards.
 */
function EntrarPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let done = false;

    async function run() {
      try {
        if (token) {
          await api.post("/api/auth/redeem-access", { token });
        }
        const me = await fetchMe();
        if (done) return;
        queryClient.setQueryData(meQueryKey, me);
        if (me) {
          void navigate({ to: "/bem-vindo", replace: true });
        } else {
          void navigate({ to: "/", replace: true, search: { sso: "erro" } });
        }
      } catch (err) {
        if (done) return;
        const message =
          err instanceof ApiError
            ? err.message
            : "Não conseguimos concluir seu acesso. Peça um novo link.";
        setError(message);
      }
    }

    void run();
    return () => {
      done = true;
    };
  }, [navigate, queryClient, token]);

  return (
    <main className="relative flex min-h-screen items-center justify-center px-4 py-10">
      <AbigailBackdrop dim />
      <section className="border-border bg-card shadow-card-deep relative w-full max-w-sm rounded-2xl border px-7 py-10 text-center backdrop-blur-md">
        <Wordmark className="text-2xl" />
        {error ? (
          <>
            <p role="alert" className="text-destructive mt-6 text-sm">
              {error}
            </p>
            <button
              type="button"
              onClick={() => void navigate({ to: "/", replace: true })}
              className="bg-ember shadow-ember mt-6 inline-flex h-11 items-center justify-center rounded-md px-5 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Pedir um novo link
            </button>
          </>
        ) : (
          <>
            <Loader2 className="text-primary mx-auto mt-7 h-6 w-6 animate-spin" />
            <p className="text-muted-foreground mt-5 text-sm">Concluindo seu acesso…</p>
          </>
        )}
      </section>
    </main>
  );
}
